// Zustellung eines Stempels mit Offline-Garantie — selbe „durabel zuerst"-Linie wie die Zeitbuchung (W2):
// das Item geht IMMER zuerst in die Offline-Outbox, dann der Online-Versuch. Der client_uuid wird einmal
// erzeugt und im Direkt- wie im Replay-Pfad genutzt → der Server dedupliziert (UNIQUE(user_id, client_uuid)).
//
// 409 ist hier KEIN Dead-Letter: ein Versions-/Zustandskonflikt heißt „Stand hat sich geändert" → die UI
// reconciled (frisch laden) und der Nutzer stempelt erneut; das Item wird aus der Live-Queue entfernt.
// Beim Offline-Flush behandelt flushOutbox einen 409 dagegen als terminalen 4xx → Dead-Letter (nie still weg).

import { ApiError, api } from '../api/client'
import { deadLetter, enqueue, removeFromOutbox } from '../offline/outbox'
import { newUuid } from '../offline/uuid'
import { noteServerNow, serverAdjustedNow } from '../offline/serverClock'
import type { AttendanceState, StampKind } from '@wf/shared'

export type StampOutcome =
  | { result: 'sent'; state: AttendanceState }
  | { result: 'converted'; correctionId: number; state: AttendanceState }
  | { result: 'queued' }

export async function submitStamp(kind: StampKind, version: number, clientUuid: string = newUuid()): Promise<StampOutcome> {
  // stamped_at = echte Tipp-Zeit (serverkorrigiert), EINMAL beim Tippen erfasst und in die Outbox eingefroren.
  // Der Server leitet daraus die Provenienz ab: sofort zugestellt → live (stamped_at ≈ now); Stunden später aus
  // der Outbox nachgereicht → outbox_replay/Korrektur an der REALEN Position statt an der Empfangszeit (R2/1.4).
  const body = { kind, version, client_uuid: clientUuid, stamped_at: serverAdjustedNow() }
  const item = { id: clientUuid, method: 'POST' as const, path: 'attendance', body }

  enqueue(item) // durabel zuerst — überlebt Crash/Reload/Standby zwischen hier und dem Server-ACK
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { result: 'queued' }

  try {
    const res = await api.stamp(body)
    removeFromOutbox(item.id)
    if ('converted' in res) {
      // Server hat den Stempel in einen Auto-Korrekturantrag umgewandelt (zu alt / kippt den Bestand):
      // KEIN neuer Stempel, der Anwesenheitsstand (res.state) bleibt wie zuvor — nur die Korrekturliste wächst.
      noteServerNow(res.state.serverNow)
      return { result: 'converted', correctionId: res.correctionId, state: res.state }
    }
    noteServerNow(res.serverNow) // Uhren-Offset am frischen Server-Zeitpunkt nachziehen (nächster Stempel)
    return { result: 'sent', state: res }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) throw err // Session weg → in der Queue lassen, zentrales 401-Handling
      if (err.status === 409) {
        removeFromOutbox(item.id) // Konflikt → UI reconciled, kein Dead-Letter (Nutzer ist am Gerät)
        throw err
      }
      if (err.status < 500) {
        deadLetter(item.id) // sonstiger terminaler 4xx → durabel sichern, sichtbar machen
        throw err
      }
    }
    return { result: 'queued' } // Netzfehler / transienter 5xx → bleibt in der Outbox
  }
}
